import axios from 'axios';
import { url } from '../env';
import { buildFunnelSteps } from './funnelSteps';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const FUNNEL_STATUS_LABELS = {
    docs_pending: 'Faltan documentos',
};

/**
 * Reemplaza `row.steps` (que el backend ya emite, pero en formato legacy de
 * 8 slots con placeholders 'na') por el embudo unificado de 7 stages que
 * usa el StepperFunnel — el mismo que la vista Buró. El backend manda los
 * campos crudos (`funnel_status`, `current_step_type`, …) específicamente
 * para esto.
 */
export const enrichRow = (row) => {
    if (!row || typeof row !== 'object') return row;
    const statusCode = row.funnel_status ?? row.status ?? 'analysis';
    const isDocsPending = statusCode === 'docs_pending';
    return {
        ...row,
        user_id: row.user_id ?? row.client?.id ?? null,
        client: row.client
            ? { ...row.client, id: row.client.id ?? row.user_id ?? null }
            : row.client,
        budget_id: row.budget_id ?? row.id,
        is_hidden: !!row.is_hidden,
        is_rejected: !!row.is_rejected,
        has_quote_process: !!row.has_quote_process,
        status: isDocsPending ? 'docs_pending' : row.status,
        status_label: FUNNEL_STATUS_LABELS[statusCode] ?? row.status_label,
        action_text: isDocsPending ? 'Faltan documentos básicos' : row.action_text,
        steps: buildFunnelSteps({
            statusCode,
            currentStepType:        row.current_step_type        ?? null,
            currentStepStatus:      row.current_step_status      ?? null,
            currentStepCompletedAt: row.current_step_completed_at ?? null,
            rejectedAtStep:         row.rejected_at_step         ?? null,
            docsHasWarnings:        Array.isArray(row.docs_warnings) && row.docs_warnings.length > 0,
        }),
        // _raw alimenta el popup de "ver más" en CotizacionRow (docs_sections /
        // missing_docs). Sin esto, la celda de acción no es clickeable en los
        // dashboards que usan este endpoint (Admin Trim, Vendedor Trim).
        _raw: {
            docs_sections:  row.docs_sections  ?? null,
            missing_docs:   row.missing_docs   ?? null,
            docs_counts:    row.docs_counts    ?? null,
            docs_warnings:  row.docs_warnings  ?? null,
        },
    };
};

export async function fetchCotizaciones(params = {}, { signal } = {}) {
    const response = await axios.get(`${url}/cotizaciones`, {
        params,
        signal,
        headers: authHeaders(),
    });
    const payload = response.data;
    if (Array.isArray(payload?.data)) {
        return { ...payload, data: payload.data.map(enrichRow) };
    }
    return payload;
}

export async function assignCotizacionAdvisor(budgetId, advisorId) {
    const response = await axios.patch(
        `${url}/cotizaciones/${budgetId}/assign`,
        { advisor_id: advisorId },
        { headers: authHeaders() },
    );
    return response.data;
}

export async function rejectCotizacion(budgetId, comment = null) {
    const response = await axios.patch(
        `${url}/cotizaciones/${budgetId}/reject`,
        { comment },
        { headers: authHeaders() },
    );
    const payload = response.data?.data ?? response.data;
    return enrichRow(payload);
}

export async function unrejectCotizacion(budgetId) {
    const response = await axios.patch(
        `${url}/cotizaciones/${budgetId}/unreject`,
        {},
        { headers: authHeaders() },
    );
    const payload = response.data?.data ?? response.data;
    return enrichRow(payload);
}

export async function toggleHideCotizacion(budgetId) {
    const response = await axios.patch(
        `${url}/cotizaciones/${budgetId}/toggle-hide`,
        {},
        { headers: authHeaders() },
    );
    return response.data?.data ?? response.data;
}

export async function changeCotizacionSource(budgetId, source) {
    const response = await axios.patch(
        `${url}/cotizaciones/${budgetId}/change-source`,
        { source },
        { headers: authHeaders() },
    );
    const payload = response.data?.data ?? response.data;
    return enrichRow(payload);
}

export async function fetchVendedoresTrim({ signal } = {}) {
    const response = await axios.get(`${url}/vendedores-trim`, {
        signal,
        headers: authHeaders(),
    });
    return response.data;
}

export async function fetchVendedores({ signal } = {}) {
    const response = await axios.get(`${url}/vendedores`, {
        signal,
        headers: authHeaders(),
    });
    return response.data;
}
